import { Card, FormattedMessageDiv, FormCheckbox, useWizardContext, FormWrapper, Navigation, LoadingAnimation } from '@odmonorepo/core-components';
import { Stack } from 'react-bootstrap';
import * as yup from 'yup';
import * as rfc6902 from 'rfc6902';
import { steps } from '../LoanAppForm';
import { patchAppDataOpts, useApplication } from 'merchant-api/unified';
import { useFormContext } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';

export const ConsentSchema = yup.object().shape({
  creditAndESignConsent: yup.boolean().oneOf([true]),
});

export const ConsentCard = ({applicationData}) => {
  const form = useFormContext();
  const { goToStep } = useWizardContext<typeof steps>();
  const { mutateAsync: patch } = useMutation({
    ...patchAppDataOpts(),
    onSuccess: () => goToStep('APP_REVIEW'),
  });

  const { isLoadingApplication } = useApplication();

  const handleNext = async() => {
    const data = form.getValues() as any;

    // Send creditAndESignConsent to back end for both creditConsent and eSignConsent
    const combined = Boolean(data?.creditAndESignConsent);
    data.creditConsent = combined;
    data.eSignConsent = combined;

    // Do not percist UI-only field
    delete data.creditAndESignConsent;

    // Create a timestamp for tlpConsnet (or null if box is unchecked)
    data.tlpConsent = data?.tlpConsent ? new Date().toISOString() : null;

    const patches = rfc6902.createPatch(applicationData?.data, data);
    await patch({id: applicationData.id, patches});
  }

  if (isLoadingApplication) return <LoadingAnimation className="mx-auto mt-7" />;


  return (
    <Stack gap={6} style={{maxWidth: '600px', margin: 'auto'}}>
      <FormattedMessageDiv className="h3 text-center" id="loanApp.step.consent"/>
      <Card bodyClassName="px-md-8">
        <Stack gap={5}>
          <FormattedMessageDiv id={'loanApp.step.consent.heading'} className='h4'/>
          <FormCheckbox name="creditAndESignConsent" data-testid="creditAndESignConsent" className="fs-7" label={<CreditAndESignDisclaimer/>}/>
          <FormCheckbox name="tlpConsent" data-testid="tlpConsent" className="fs-7" label={<TlpDisclaimer/>}/>
          <Navigation stacked handleNext={handleNext} continueOnly={applicationData?.metadata?.channelCode === 'PARTNER_STRATEGIC'} />
        </Stack>
      </Card>
      <FormattedMessageDiv className="fs-8" id="loanApp.disclaimer" />
    </Stack>
  );
};

const CreditAndESignDisclaimer = () =>
  <FormattedMessageDiv id={`formCheckbox.creditAndESignConsent`} values={{
    terms: (txt: any) => <a target="_blank" rel="noreferrer" href="https://www.ondeck.com/terms">{txt}</a>,
    credit: (txt: any) => <a target="_blank" rel="noreferrer" href={`https://www.ondeck.com/creditagreement`}>{txt}</a>,
    eSign: (txt: any) => <a target="_blank" rel="noreferrer" href={`https://www.ondeck.com/esignagreement`}>{txt}</a>
  }}/>

const TlpDisclaimer = () =>
  <FormattedMessageDiv id="formCheckbox.tlpConsent" values={{
      terms: (txt: any) => <a target="_blank" rel="noreferrer" href={`https://www.ondeck.com/odc-terms`}>{txt}</a>
    }}
  />


  export const Consent = () => {
  const { applicationData } = useApplication();

  const mapDefaults = (data: any) => {
    if (!data) return data;
    return {
      ...data,
      // Business rule says these are always both true or both false
      creditAndESignConsent: Boolean(data?.creditConsent && data?.eSignConsent),
    };
  };

  return (
    <FormWrapper schema={ConsentSchema} defaultValues={mapDefaults(applicationData?.data)}>
      <ConsentCard applicationData={applicationData} />
    </FormWrapper>
  );
};


export default Consent;
